import { useEffect, useRef, useState } from 'react'
import { z } from 'zod'
import {
  HandoverAuthority,
  HandoverPreparation,
  HandoverBundle,
  HandoverReceipt,
  InstallationDescriptor,
  type THandoverBundle,
} from '@schemas'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  useInstallationIdentity,
  useOwnership,
  useTransferAction,
  transferApi,
  isUncertainTransferFailure,
  transferRequest,
  downloadJson,
} from '@/api/show-transfer'
import { Check, JsonFile, privacyWarning } from './files'
import PackageReview from './PackageReview'
import { PrivacyOptions, StatusNotice, WorkflowFooter, WorkflowHeading } from './WorkflowLayout'

type Profile = 'limited' | 'full-contact'
export default function HandoverControls({
  showId,
  onLocked,
  disabled = false,
  guided = false,
  role = 'source',
  recoveryOnly = false,
}: {
  showId: string
  guided?: boolean
  role?: 'source' | 'destination'
  recoveryOnly?: boolean
  disabled?: boolean
  onLocked?: (locked: boolean) => void
}) {
  const [stage, setStage] = useState(0)
  useEffect(() => {
    setStage(recoveryOnly ? 5 : 0)
  }, [role, recoveryOnly])
  const stages =
    role === 'source'
      ? [
          { id: 0, label: 'Verify servers' },
          { id: 2, label: 'Pause & send' },
          { id: 4, label: 'Confirm receipt' },
        ]
      : [
          { id: 0, label: 'Verify servers' },
          { id: 1, label: 'Prepare' },
          { id: 3, label: 'Review & activate' },
        ]
  const identity = useInstallationIdentity(),
    ownership = useOwnership(showId)
  const [reading, setReading] = useState(false)
  const [reviewLocked, setReviewLocked] = useState(false)
  const [peer, setPeer] = useState<z.infer<typeof InstallationDescriptor>>()
  const [verified, setVerified] = useState(false)
  const [authority, setAuthority] = useState<z.infer<typeof HandoverAuthority>>()
  const [preparation, setPreparation] = useState<z.infer<typeof HandoverPreparation>>()
  // A recovered prepared destination, kept so each loaded source authority rebuilds the preparation.
  // The server checks the authority against the intent only at freeze, so a later file replaces it.
  const [recoveredPreparation, setRecoveredPreparation] = useState<{
    intent: unknown
    checkpoint: unknown
  }>()
  const [unsavedPreparation, setUnsavedPreparation] = useState(false)
  const [unsavedBundle, setUnsavedBundle] = useState(false)
  const [unsavedReceipt, setUnsavedReceipt] = useState(false)
  const unsavedResult = unsavedBundle || unsavedReceipt
  const [bundle, setBundle] = useState<THandoverBundle>()
  const [receipt, setReceipt] = useState<z.infer<typeof HandoverReceipt>>()
  const [transferKey, setTransferKey] = useState('')
  useEffect(() => {
    if (ownership.data?.transferKey && !transferKey) setTransferKey(ownership.data.transferKey)
  }, [ownership.data?.transferKey, transferKey])
  const [profile, setProfile] = useState<Profile>('limited')
  const [contacts, setContacts] = useState(false)
  const [synced, setSynced] = useState(false)
  const [sole, setSole] = useState(false)
  const [cancelConfirmed, setCancelConfirmed] = useState(false)
  const [message, setMessage] = useState('')
  const action = useTransferAction(async (operation: () => Promise<unknown>) => operation())
  const [uncertain, setUncertain] = useState(false)
  const retryOperation = useRef<{
    operation: () => Promise<unknown>
    success: string | ((result: unknown) => string)
  } | null>(null)
  const running = useRef(false)
  useEffect(() => {
    onLocked?.(
      action.isPending ||
        reviewLocked ||
        reading ||
        uncertain ||
        unsavedPreparation ||
        unsavedResult
    )
  }, [
    action.isPending,
    reviewLocked,
    reading,
    uncertain,
    unsavedPreparation,
    unsavedResult,
    onLocked,
  ])
  function clearChain() {
    setPreparation(undefined)
    setRecoveredPreparation(undefined)
    setBundle(undefined)
    setReceipt(undefined)
    setSynced(false)
    setContacts(false)
    setCancelConfirmed(false)
    setMessage('')
  }
  async function run(
    operation: () => Promise<unknown>,
    success: string | ((result: unknown) => string)
  ) {
    if (running.current || disabled || reviewLocked || reading) return
    running.current = true
    retryOperation.current = { operation, success }
    setMessage('')
    try {
      const result = await action.mutateAsync(operation)
      setUncertain(false)
      setMessage(typeof success === 'function' ? success(result) : success)
    } catch (e) {
      setUncertain(isUncertainTransferFailure(e))
      setMessage(
        `${e instanceof Error ? e.message : 'Operation failed'}. Preserve files and recover the transfer before retrying. A missing response never permits source reactivation.`
      )
    } finally {
      running.current = false
    }
  }
  const canDownload = profile === 'limited' || contacts
  const validTransferKey = z.uuid().safeParse(transferKey).success
  async function recover() {
    const key = z.uuid().parse(transferKey)
    clearChain()
    const value = z
      .object({
        state: z.string(),
        intent: z.unknown(),
        checkpoint: z.unknown(),
        grant: z.unknown(),
        package: z.unknown(),
        receipt: z.unknown(),
        authority: z.unknown().optional(),
        accepted: z.unknown(),
      })
      .parse(await transferRequest(`show-handovers/${key}`))
    if (value.receipt) setReceipt(HandoverReceipt.parse(value.receipt))
    if (value.grant && value.authority)
      setBundle(
        HandoverBundle.parse({
          intent: value.intent,
          checkpoint: value.checkpoint,
          authority: value.authority,
          grant: value.grant,
          package: value.package,
        })
      )
    // Only a still-prepared destination can be frozen; a withdrawn one keeps its intent but is terminal.
    else if (value.state === 'prepared' && value.intent && !value.grant) {
      const recovered = { intent: value.intent, checkpoint: value.checkpoint }
      setRecoveredPreparation(recovered)
      if (authority) setPreparation(HandoverPreparation.parse({ ...recovered, authority }))
    }
    return value.state
  }
  return (
    <section className="space-y-5" aria-label="File handover">
      {!guided && <h2 className="text-xl font-semibold">Move a show using files</h2>}
      {guided && !recoveryOnly && (
        <nav aria-label="File transfer progress" className="flex flex-wrap gap-4 border-b pb-3">
          {stages.map((item, index) => (
            <Button
              key={item.id}
              variant={stage === item.id ? 'secondary' : 'ghost'}
              aria-current={stage === item.id ? 'step' : undefined}
              disabled={
                action.isPending ||
                reviewLocked ||
                reading ||
                uncertain ||
                unsavedPreparation ||
                unsavedResult ||
                disabled
              }
              onClick={() => setStage(item.id)}
            >
              {index + 1}. {item.label}
            </Button>
          ))}
        </nav>
      )}
      {guided && (
        <WorkflowHeading>
          {
            (
              {
                0: 'Verify server identities',
                1: 'Prepare this server to receive',
                2: 'Pause editing and send the show',
                3: 'Review and activate the show',
                4: 'Confirm the transfer receipt',
                5: 'Recover or cancel a transfer',
              } as Record<number, string>
            )[stage]
          }
        </WorkflowHeading>
      )}
      <StatusNotice
        tone="warning"
        title={
          stage === 5 ? 'Recover before starting again' : 'Coordinate with the other administrator'
        }
      >
        {stage === 5
          ? 'An unknown result must be recovered. A missing receipt never permits the original server to resume editing.'
          : 'Only one server can run the show at a time. Verify the servers, then sync every intake device before pausing editing.'}
      </StatusNotice>
      <details>
        <summary className="text-sm text-muted-foreground">About transfer files</summary>
        <p className="mt-2 text-sm">{privacyWarning}</p>
      </details>
      {showId && (!guided || (role === 'source' && !recoveryOnly && stage !== 5)) && (
        <details>
          <summary className="text-sm font-medium">
            Current editing status and source authority
          </summary>
          <div className="mt-3 space-y-3">
            <p role="status">
              Ownership: {ownership.data?.state ?? (ownership.isError ? 'unavailable' : 'loading')}{' '}
              {ownership.data &&
                `· epoch ${ownership.data.epoch} · ${ownership.data.canOperate ? 'operations allowed' : 'read-only'}`}
            </p>
            {ownership.data?.canDesignate && (
              <>
                <Check checked={sole} onChange={setSole}>
                  I confirm this is the only active copy of this show.
                </Check>
                <Button
                  disabled={
                    disabled ||
                    !sole ||
                    action.isPending ||
                    uncertain ||
                    unsavedPreparation ||
                    unsavedResult
                  }
                  onClick={() =>
                    void run(
                      () =>
                        transferRequest(`shows/${encodeURIComponent(showId)}/ownership/designate`, {
                          confirmOnlyActiveCopy: true,
                        }),
                      'This copy is now operational.'
                    )
                  }
                >
                  Designate this copy active
                </Button>
              </>
            )}
            {ownership.data?.authority && (
              <Button
                variant="outline"
                onClick={() => downloadJson(ownership.data!.authority, 'show-authority.json')}
              >
                Download source authority
              </Button>
            )}
          </div>
        </details>
      )}
      <fieldset
        hidden={guided && stage !== 0}
        disabled={
          disabled ||
          action.isPending ||
          reviewLocked ||
          reading ||
          uncertain ||
          unsavedPreparation ||
          unsavedResult
        }
        className="space-y-4"
      >
        <legend className={guided ? 'sr-only' : 'font-semibold'}>
          1. Verify installation identities
        </legend>
        <p>
          Exchange identity files and independently verify the installation and fingerprint with the
          other administrator. Pin both the original authority root and current sender when they
          differ. Never trust an identity solely because it arrived in a bundle.
        </p>
        {identity.data && (
          <>
            <p className="break-all">
              This installation: {identity.data.installationKey}
              <br />
              Fingerprint: {identity.data.fingerprint}
            </p>
            <Button
              variant="outline"
              onClick={() => downloadJson(identity.data, 'installation-identity.json')}
            >
              Download installation identity
            </Button>
          </>
        )}
        <JsonFile
          onReading={setReading}
          label="Peer installation identity"
          onStart={() => {
            setPeer(undefined)
            setVerified(false)
          }}
          onLoad={value => {
            setPeer(InstallationDescriptor.parse(value))
            setVerified(false)
          }}
        />
        {peer && (
          <>
            <p className="break-all">
              Peer: {peer.installationKey}
              <br />
              Fingerprint: {peer.fingerprint}
            </p>
            <Check checked={verified} onChange={setVerified}>
              I independently verified this installation and fingerprint.
            </Check>
            <WorkflowFooter>
              <Button
                disabled={!verified}
                onClick={() =>
                  void run(
                    () => transferRequest('show-handover-peers', peer),
                    'Verified peer identity pinned.'
                  )
                }
              >
                Pin verified identity
              </Button>
            </WorkflowFooter>
          </>
        )}
      </fieldset>
      <fieldset
        hidden={guided && stage !== 1}
        disabled={disabled || action.isPending || reviewLocked || reading || uncertain}
        className="space-y-3"
      >
        <legend className={guided ? 'sr-only' : 'font-semibold'}>
          2. Prepare on the destination
        </legend>
        <JsonFile
          onReading={setReading}
          disabled={unsavedPreparation || unsavedResult}
          label="Source authority file"
          onStart={() => {
            setAuthority(undefined)
            clearChain()
          }}
          onLoad={value => {
            setAuthority(HandoverAuthority.parse(value))
            setTransferKey('')
            setPreparation(undefined)
          }}
        />
        <PrivacyOptions
          value={profile}
          disabled={unsavedPreparation || unsavedResult}
          onChange={value => {
            setProfile(value)
            setContacts(false)
          }}
        />
        {profile === 'full-contact' && (
          <Check checked={contacts} onChange={setContacts}>
            I acknowledge that contact details will be transferred in plaintext files.
          </Check>
        )}
        <WorkflowFooter>
          <Button
            disabled={!authority || !canDownload || unsavedPreparation || unsavedResult}
            onClick={() => {
              const key = transferKey || crypto.randomUUID()
              setTransferKey(key)
              void run(async () => {
                setPreparation(
                  await transferApi.prepare({
                    authority,
                    transferKey: key,
                    privacyProfile: profile,
                  })
                )
                setUnsavedPreparation(true)
              }, 'Destination prepared. Save the preparation and take it to the source.')
            }}
          >
            Prepare destination
          </Button>
        </WorkflowFooter>
        {preparation && (
          <Button
            variant="outline"
            disabled={preparation.intent.body.privacyProfile === 'full-contact' && !contacts}
            onClick={() => {
              downloadJson(preparation, 'handover-preparation.json')
              setUnsavedPreparation(false)
            }}
          >
            Download preparation
          </Button>
        )}
      </fieldset>
      <fieldset
        hidden={guided && stage !== 2}
        disabled={
          disabled || action.isPending || reviewLocked || reading || uncertain || unsavedPreparation
        }
        className="space-y-3"
      >
        <legend className={guided ? 'sr-only' : 'font-semibold'}>3. Freeze on the source</legend>
        <JsonFile
          onReading={setReading}
          disabled={unsavedResult}
          label="Destination preparation file"
          onStart={clearChain}
          onLoad={value => {
            const parsed = HandoverPreparation.parse(value)
            setPreparation(parsed)
            setTransferKey(parsed.intent.body.transferKey)
            setSynced(false)
            setContacts(false)
          }}
        />
        <StatusNotice tone="warning" title="Editing will pause on the source">
          Sync every intake device first. This is your operator attestation, not automatic
          inspection of devices. Pending browser entries must be preserved.
        </StatusNotice>
        <Check checked={synced} onChange={setSynced}>
          All intake devices are synced, and I will stop operations on the source.
        </Check>
        {(preparation?.intent.body.privacyProfile === 'full-contact' ||
          bundle?.intent.body.privacyProfile === 'full-contact') && (
          <Check checked={contacts} onChange={setContacts}>
            I acknowledge this bundle includes contact details in plaintext.
          </Check>
        )}
        <WorkflowFooter>
          <Button
            variant="destructive"
            disabled={
              unsavedResult ||
              !showId ||
              !preparation ||
              !synced ||
              (preparation.intent.body.privacyProfile === 'full-contact' && !contacts)
            }
            onClick={() =>
              void run(async () => {
                setBundle(
                  await transferApi.freeze(showId, { preparation, intakeDevicesSynced: true })
                )
                setUnsavedBundle(true)
              }, 'Source frozen. Download the bundle and take it to the destination.')
            }
          >
            Freeze source
          </Button>
        </WorkflowFooter>
        {bundle && (
          <Button
            variant="outline"
            disabled={bundle.intent.body.privacyProfile === 'full-contact' && !contacts}
            onClick={() => {
              downloadJson(bundle, 'handover-bundle.json')
              setUnsavedBundle(false)
            }}
          >
            Download handover bundle
          </Button>
        )}
      </fieldset>
      <section hidden={guided && stage !== 3} className="space-y-3">
        <h3 className={guided ? 'sr-only' : 'font-semibold'}>
          4. Review and activate on the destination
        </h3>
        <JsonFile
          onReading={setReading}
          disabled={
            disabled ||
            reviewLocked ||
            action.isPending ||
            uncertain ||
            reading ||
            unsavedPreparation ||
            unsavedResult
          }
          label="Signed handover bundle"
          onStart={clearChain}
          onLoad={value => {
            const parsed = HandoverBundle.parse(value)
            setBundle(parsed)
            setTransferKey(parsed.intent.body.transferKey)
            setCancelConfirmed(false)
          }}
        />
        {bundle &&
          identity.data &&
          bundle.intent.body.to.installationKey === identity.data.installationKey && (
            <PackageReview
              disabled={disabled || action.isPending || reading || uncertain || unsavedPreparation}
              onLocked={setReviewLocked}
              onFinished={() => setUnsavedReceipt(true)}
              onReceiptSaved={() => setUnsavedReceipt(false)}
              key={bundle.grant.signature}
              bundle={bundle}
              packageValue={bundle.package}
              installationKey={identity.data.installationKey}
            />
          )}
      </section>
      <fieldset
        hidden={guided && stage !== 4}
        disabled={
          disabled || action.isPending || reviewLocked || reading || uncertain || unsavedPreparation
        }
        className="space-y-3"
      >
        <legend className={guided ? 'sr-only' : 'font-semibold'}>
          5. Return the receipt to the source
        </legend>
        <JsonFile
          onReading={setReading}
          disabled={unsavedResult}
          label="Activation or cancellation receipt"
          onStart={() => {
            setReceipt(undefined)
            setMessage('')
          }}
          onLoad={value => {
            setReceipt(HandoverReceipt.parse(value))
          }}
        />
        <WorkflowFooter>
          <Button
            disabled={!receipt || unsavedResult}
            onClick={() =>
              void run(
                () => transferRequest('show-handovers/receipt', receipt),
                'Receipt accepted. Ownership status refreshed.'
              )
            }
          >
            Submit receipt
          </Button>
        </WorkflowFooter>
        {receipt && (
          <Button
            variant="outline"
            onClick={() => {
              downloadJson(receipt, 'handover-receipt.json')
              setUnsavedReceipt(false)
            }}
          >
            Download saved receipt
          </Button>
        )}
      </fieldset>
      <fieldset
        hidden={guided && stage !== 5}
        disabled={disabled || action.isPending || reviewLocked || reading || uncertain}
        className="space-y-3"
      >
        <legend className={guided ? 'sr-only' : 'font-semibold'}>
          Recover or cancel a transfer
        </legend>
        <p>Record the transfer ID before leaving. Recovery never changes ownership by itself.</p>
        <label>
          Transfer ID
          <Input
            disabled={unsavedPreparation || unsavedResult}
            value={transferKey}
            onChange={e => setTransferKey(e.target.value)}
          />
        </label>
        <Button
          disabled={!validTransferKey || unsavedPreparation || unsavedResult}
          onClick={() =>
            void run(
              recover,
              state =>
                `Recovered transfer: ${state}. Save available artifacts. For a prepared destination, load the original source authority to reconstruct preparation. A destination receipt can be returned without reconstructing its bundle.`
            )
          }
        >
          Recover transfer artifacts
        </Button>
        {guided && (
          <div className="space-y-4">
            <details>
              <summary>Restore files for this transfer</summary>
              <div className="mt-3 space-y-3">
                <JsonFile
                  label="Original source authority file"
                  disabled={unsavedPreparation || unsavedResult}
                  onReading={setReading}
                  onLoad={value => {
                    const loaded = HandoverAuthority.parse(value)
                    // Parse before changing state, so a mismatched file keeps the recovery.
                    const rebuilt =
                      recoveredPreparation &&
                      HandoverPreparation.parse({ ...recoveredPreparation, authority: loaded })
                    setAuthority(loaded)
                    if (rebuilt) setPreparation(rebuilt)
                  }}
                />
                <JsonFile
                  label="Original handover bundle for cancellation"
                  disabled={unsavedPreparation || unsavedResult}
                  onReading={setReading}
                  onLoad={value => {
                    const saved = HandoverBundle.parse(value)
                    clearChain()
                    setBundle(saved)
                    setTransferKey(saved.intent.body.transferKey)
                  }}
                />
              </div>
            </details>
            {(bundle?.intent.body.privacyProfile === 'full-contact' ||
              preparation?.intent.body.privacyProfile === 'full-contact') && (
              <Check checked={contacts} onChange={setContacts}>
                I understand recovered files include unencrypted contact details.
              </Check>
            )}
            {preparation && !bundle && (
              <Button
                variant="outline"
                disabled={preparation.intent.body.privacyProfile === 'full-contact' && !contacts}
                onClick={() => {
                  downloadJson(preparation, 'handover-preparation.json')
                  setUnsavedPreparation(false)
                }}
              >
                Save recovered preparation
              </Button>
            )}
            {bundle && (
              <Button
                variant="outline"
                disabled={bundle.intent.body.privacyProfile === 'full-contact' && !contacts}
                onClick={() => {
                  downloadJson(bundle, 'handover-bundle.json')
                  setUnsavedBundle(false)
                }}
              >
                Save recovered transfer bundle
              </Button>
            )}
            {receipt && (
              <Button
                variant="outline"
                onClick={() => {
                  downloadJson(receipt, 'handover-receipt.json')
                  setUnsavedReceipt(false)
                }}
              >
                Save recovered receipt
              </Button>
            )}
          </div>
        )}
        <p>
          Before activation, cancel at the destination and return its signed cancellation receipt to
          resume the source. After activation, use a new return handover. An unknown destination
          outcome requires recovery; there is no forced takeover.
        </p>
        <Check checked={cancelConfirmed} onChange={setCancelConfirmed}>
          I intend to cancel this grant or withdraw this destination preparation.
        </Check>
        <Button
          variant="destructive"
          disabled={!bundle || !cancelConfirmed || unsavedResult}
          onClick={() =>
            void run(async () => {
              setReceipt(
                HandoverReceipt.parse(await transferRequest('show-handovers/cancel', bundle))
              )
              setUnsavedReceipt(true)
            }, 'Grant cancelled. Return the signed receipt to the source.')
          }
        >
          Cancel grant at destination
        </Button>
        <Button
          variant="destructive"
          disabled={!validTransferKey || !cancelConfirmed || unsavedResult}
          onClick={() =>
            void run(async () => {
              await transferRequest(`show-handovers/${transferKey}/withdraw`, {})
              setUnsavedPreparation(false)
              setPreparation(undefined)
              setRecoveredPreparation(undefined)
            }, 'Preparation withdrawn. If the source froze, obtain its bundle and cancel it here, then return the cancellation receipt. Withdrawal alone never resumes the source.')
          }
        >
          Withdraw preparation
        </Button>
      </fieldset>
      {unsavedPreparation && (
        <StatusNotice tone="warning" role="alert" title="Save the preparation before leaving">
          Download the preparation or withdraw it before leaving or replacing artifacts. Transfer
          ID: {transferKey}
        </StatusNotice>
      )}
      {unsavedResult && (
        <StatusNotice tone="warning" role="alert" title="Save the transfer file before leaving">
          {unsavedReceipt
            ? 'Save the signed receipt to return it to the source.'
            : 'Save the handover bundle to continue on the destination.'}{' '}
          Transfer ID: {transferKey}
        </StatusNotice>
      )}
      {uncertain && (
        <StatusNotice tone="warning" role="alert" title="The result is not confirmed">
          <p>
            The outcome is unknown. Keep the current artifacts and retry this exact action to
            recover its result before starting another transfer.
          </p>
          <Button
            disabled={disabled || action.isPending}
            onClick={() => {
              const saved = retryOperation.current
              if (saved) void run(saved.operation, saved.success)
            }}
          >
            Retry interrupted action
          </Button>
        </StatusNotice>
      )}
      {action.isPending && <p role="status">Working… Keep this page open.</p>}
      {message && (
        <StatusNotice
          tone={action.isError ? (uncertain ? 'warning' : 'error') : 'success'}
          role={action.isError ? 'alert' : 'status'}
        >
          {message}
        </StatusNotice>
      )}
      {guided && (
        <>
          {!recoveryOnly &&
            stage !== 5 &&
            stages.findIndex(item => item.id === stage) < stages.length - 1 && (
              <Button
                variant="link"
                disabled={
                  action.isPending ||
                  reviewLocked ||
                  reading ||
                  uncertain ||
                  unsavedPreparation ||
                  unsavedResult ||
                  disabled
                }
                onClick={() => setStage(stages[stages.findIndex(item => item.id === stage) + 1].id)}
              >
                Continue to{' '}
                {stages[stages.findIndex(item => item.id === stage) + 1].label.toLowerCase()}
              </Button>
            )}
          {!recoveryOnly && (
            <Button
              variant="link"
              disabled={
                action.isPending ||
                reviewLocked ||
                reading ||
                uncertain ||
                unsavedResult ||
                disabled
              }
              onClick={() => setStage(stage === 5 ? (unsavedPreparation ? 1 : 0) : 5)}
            >
              {stage === 5 ? 'Back to transfer steps' : 'Recover or cancel this transfer'}
            </Button>
          )}
          {!recoveryOnly && stage === 0 && (
            <p className="text-sm text-muted-foreground">
              {role === 'source'
                ? 'Next, send the source authority file to the receiving administrator. They prepare the destination and return a preparation file.'
                : 'Ask the sending administrator for the source authority file before preparing this destination.'}
            </p>
          )}
          {!recoveryOnly && stage === 2 && (
            <p className="text-sm text-muted-foreground">
              After saving the bundle, send it to the receiving administrator. They review and
              activate it, then return a signed receipt.
            </p>
          )}
          {!recoveryOnly && stage === 3 && (
            <p className="text-sm text-muted-foreground">
              Return the signed receipt to the sending administrator so they can confirm the
              transfer.
            </p>
          )}
        </>
      )}
    </section>
  )
}
